import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Input,
  Select,
  Table,
  Space,
  Tooltip,
  message,
  Popconfirm,
  Modal,
} from "antd";
import dayjs from "dayjs";
import {
  PlusOutlined,
  FileExcelOutlined,
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import axiosInstance, { baseURL } from "../../configs"; // sesuaikan path
import { getUser } from "../../utils/DataUser";

const { Title, Text } = Typography;
const { Option } = Select;

const defaultPageSize = 20;

export default function BrowseItem() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: defaultPageSize,
    total: 0,
  });

  // filter
  const [searchName, setSearchName] = useState("");
  const [searchCode, setSearchCode] = useState("");

  const [pdfUrl, setPdfUrl] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);

  const [deletingId, setDeletingId] = useState(null);

  const apiBase = process.env.REACT_APP_PDF_URL || "/api";
  console.log("API Base URL:", apiBase);
  const openPDF = (id_pelekatan_pc) => {
    const url = `/#/print/pelekatan/${id_pelekatan_pc}`;
    setPdfUrl(url);
    setShowPdfModal(true);
  };

  const fetchItems = async (page = 1, pageSize = defaultPageSize) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/pelekatan-pc", {
        params: {
          page,
          limit: pageSize,
        },
      });

      const raw = res.data || {};
      const payload = raw.data || raw;
      const rows =
        payload.items ||
        payload.rows ||
        payload.data ||
        (Array.isArray(payload) ? payload : []);

      const user = getUser();
      let finalRows = rows;
      if (user && user.id_level === 7) {
        // user.username is NPWP
        finalRows = rows.filter(item =>
          item.customer_name === user.username /* Assuming customer_name holds name/id? Check col def: customer_name */
          /* The column def says customer_name. But maybe id_customer is in record? */
          || item.id_customer === user.username
        );
      }

      const total =
        payload.total ||
        payload.count ||
        payload.pagination?.total ||
        rows.length;

      setItems(finalRows);
      setPagination({
        current: page,
        pageSize,
        total: total || 0,
      });
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat daftar pelekatan pita cukai");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(1, defaultPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchItems(1, pagination.pageSize);
  };

  const handleResetFilter = () => {
    setSearchName("");
    setSearchCode("");
    fetchItems(1, defaultPageSize);
  };

  const handleTableChange = (pager) => {
    fetchItems(pager.current, pager.pageSize);
  };

  const handleExportExcel = () => {
    window.open(`${baseURL}/pelekatan-pc/export`, "_blank");
  };

  const handleDelete = async (record) => {
    setDeletingId(record.id_pelekatan_pc);
    try {
      await axiosInstance.delete(`/pelekatan-pc/${record.id_pelekatan_pc}`);
      message.success("Pelekatan pita berhasil dihapus");
      // reload current page
      fetchItems(pagination.current, pagination.pageSize);
    } catch (err) {
      console.error(err);
      message.error("Gagal menghapus pelekatan pita");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (value) =>
    value ? dayjs(value).format("DD-MM-YYYY") : "-";

  const formatDateTime = (value) =>
    value ? dayjs(value).format("DD-MM-YYYY HH:mm") : "-";

  const columns = [
    {
      title: "NO",
      dataIndex: "index",
      width: 70,
      align: "center",
      render: (text, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "IMPORTIR",
      dataIndex: "customer_name",
      width: 130,
    },
    {
      title: "NOPEN",
      dataIndex: "nopen",
      width: 130,
    },
    {
      title: "NO BERITA ACARA",
      dataIndex: "no_ba",
      width: 130,
    },
    {
      title: "TANGGAL BERITA ACARA",
      dataIndex: "tanggal_ba",
      width: 140,
      render: (value) => formatDate(value),
    },

    {
      title: "NO CK1A",
      dataIndex: "no_ck1a",
      width: 160,
      render: (value) => value || "-",
    },

    {
      title: "AKSI",
      key: "action",
      width: 160,
      align: "center",
      render: (_, record) => (
        <Space>
          <Tooltip title="Lihat & Cetak PDF">
            <Button
              size="small"
              type="primary"
              icon={<PrinterOutlined />}
              style={{
                background: "#3699FF",
                borderColor: "#3699FF",
              }}
              onClick={() => openPDF(record.id_pelekatan_pc)}
            />
          </Tooltip>
          <Tooltip title={getUser().id_level === 7 ? "Lihat Detil" : "Edit"}>
            <Link to={`/pita/editpelekatan/${record.id_pelekatan_pc}`}>
              <Button
                size="small"
                type="primary"
                icon={getUser().id_level === 7 ? <SearchOutlined /> : <EditOutlined />}
                style={{
                  background: "#3699FF",
                  borderColor: "#3699FF",
                }}
              />
            </Link>
          </Tooltip>

          {getUser().id_level !== 7 && (
            <Popconfirm
              title="Hapus Pelekatan Pita"
              description={`Yakin ingin menghapus "${record.no_ba || record.no_ba
                }"?`}
              okText="Ya, Hapus"
              cancelText="Batal"
              okButtonProps={{
                danger: true,
                loading: deletingId === record.id_pelekatan_pc,
              }}
              onConfirm={() => handleDelete(record)}
            >
              <Tooltip title="Hapus">
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  loading={deletingId === record.id_pelekatan_pc}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
      {/* HEADER TITLE + ACTIONS */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 4 }}>
            Browse Pelekatan Pita Cukai
          </Title>
          <Text type="secondary">
            Kelola daftar Pelekatan Pita Cukai untuk kebutuhan inventory &
            kepabeanan.
          </Text>
        </Col>
        <Col>
          <Space>
            {getUser().id_level !== 7 && (
              <Link to="/pita/addpelekatan">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  style={{
                    background: "#1BC5BD",
                    borderColor: "#1BC5BD",
                    fontWeight: 600,
                  }}
                >
                  Tambah
                </Button>
              </Link>
            )}
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              style={{
                background: "#1E1EFA",
                borderColor: "#1E1EFA",
                fontWeight: 600,
              }}
            >
              Excel
            </Button>
          </Space>
        </Col>
      </Row>

      {/* FILTER BAR */}

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Text strong>Cari Berdasarkan Pemilik Barang</Text>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Cari Item..."
            style={{ marginTop: 4 }}
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>

        <Col xs={24} md={12}>
          <Text strong>Cari Berdasarkan No Pendaftaran</Text>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Cari kode..."
            style={{ marginTop: 4 }}
            value={searchCode}
            onChange={(e) => setSearchCode(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>

        <Col xs={24} style={{ textAlign: "right" }}>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleResetFilter}>
              Reset
            </Button>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              style={{
                background: "#8950FC",
                borderColor: "#8950FC",
              }}
            >
              Apply Filter
            </Button>
          </Space>
        </Col>
        <Modal
          title="Cetak Pelekatan Pita Cukai"
          open={showPdfModal}
          onCancel={() => setShowPdfModal(false)}
          width={900}
          footer={null}
          bodyStyle={{ padding: 0 }}
        >
          {pdfUrl && (
            <iframe
              src={pdfUrl}
              title="Pelekatan Pita Cukai"
              style={{
                width: "100%",
                height: "80vh",
                border: "none",
              }}
            />
          )}
        </Modal>
      </Row>

      <Table
        style={{ marginTop: 36 }}
        rowKey="id_item"
        dataSource={items}
        columns={columns}
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `Total ${total} data | ${range[0]}-${range[1]}`,
        }}
        onChange={handleTableChange}
      />
    </Card>
  );
}
